import * as React from "react"
import { getItemList } from "../utils/sidebar/item-list"
import { SidebarWidth } from "./shared/constants/layout"
import Container from "../layout/container"
import ChevronSvg from "./sidebar/chevron-svg"

// from sticky-responsive-sidebar
const MobileDisclosureButton = ({ isOpen, innerRef, ...props }) => {
  const iconOffset = isOpen ? 5 : -5

  return (
    <div
      ref={innerRef}
      {...props}
      css={theme => ({
        backgroundColor: theme.colors.gatsby,
        borderRadius: 99999,
        bottom: theme.space[6],
        boxShadow: theme.shadows.dialog,
        cursor: `pointer`,
        display: `flex`,
        height: theme.space[11],
        justifyContent: `space-around`,
        position: `fixed`,
        right: theme.space[7],
        visibility: `visible`,
        width: theme.space[11],
        zIndex: 20,
        [theme.mediaQueries.tablet]: {
          display: `none`,
        },
      })}
    >
      <div
        css={theme => ({
          alignSelf: `center`,
          color: `white`,
          display: `flex`,
          flexDirection: `column`,
          height: theme.space[6],
          visibility: `visible`,
          width: theme.space[6],
        })}
      >
        <ChevronSvg
          size={16}
          cssProps={{
            transform: `translate(${iconOffset}px, 5px) rotate(90deg)`,
            transition: t =>
              `transform ${t.transitions.speed.default} ${t.transitions.curve.default}`,
          }}
        />
        <ChevronSvg
          size={16}
          cssProps={{
            transform: `translate(${5 - iconOffset}px, -5px) rotate(270deg)`,
            transition: t =>
              `transform ${t.transitions.speed.default} ${t.transitions.curve.default}`,
          }}
        />
      </div>
    </div>
  )
}

export default function PageWithSidebar({
  children,
  location,
  SidebarComponent: Sidebar,
}) {
  const [isOpen, setIsOpen] = React.useState(false)
  const sidebarRef = React.useRef(null)
  const buttonRef = React.useRef(null)
  const handleBodyClick = e => {
    if (sidebarRef?.current.contains(e.target)) return
    if (buttonRef?.current.contains(e.target)) return
    setIsOpen(false)
  }

  React.useEffect(() => {
    document.body.addEventListener("click", handleBodyClick)
    return () => {
      document.body.removeEventListener("click", handleBodyClick)
    }
  }, [])

  const itemList = getItemList(location.pathname)

  return (
    <>
      <Container
        css={{
          paddingLeft: 0,
          paddingRight: 0,
          display: "flex",
          alignItems: "flex-start",
        }}
      >
        <div
          css={{
            minWidth: 0,
          }}
        >
          {children}
        </div>
        <div
          ref={sidebarRef}
          style={{
            transform: isOpen ? "translateX(0)" : "translateX(-100%)",
            opacity: isOpen ? 1 : 0,
          }}
          css={theme => ({
            // mobile
            height: "100vh",
            position: "fixed",
            top: 0,
            bottom: 0,
            left: 0,
            zIndex: theme.zIndices.modals,
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
            width: SidebarWidth.mobile,
            backgroundColor: theme.colors.white,
            transitionProperty: "opacity, transform",
            transitionDuration: theme.transitions.speed.default,
            transitionTimingFunction: theme.transitions.curve.default,
            [theme.mediaQueries.tablet]: {
              order: -1,
              position: "static",
              zIndex: "auto",
              height: "auto",
              // hard-coded based on existing implementation
              width: "18rem",
              flex: "none",
              // undo mobile/dynamic styles
              transform: "none !important",
              opacity: "1 !important",
            },
            "* svg": {
              // Fix for gatsby-interface component
              pointerEvents: "none",
            },
          })}
        >
          {itemList && Sidebar && (
            <Sidebar
              itemList={itemList.items}
              title={itemList.title}
              disableExpandAll={itemList.disableExpandAll}
              disableAccordions={itemList.disableAccordions}
              key={location.pathname}
              location={location}
              closeSidebar={() => setIsOpen(false)}
            />
          )}
        </div>
      </Container>
      <MobileDisclosureButton
        innerRef={buttonRef}
        isOpen={isOpen}
        onClick={() => setIsOpen(isOpen => !isOpen)}
        role="button"
        aria-label="Show Secondary Navigation"
        aria-controls="SecondaryNavigation"
        aria-expanded={isOpen ? `true` : `false`}
        tabIndex={0}
      />
    </>
  )
}
